<template>
  <div class="tracking-view">
    <MapView 
      :tracks="runnerData?.tracks" 
      :latest-position="runnerData?.latestPosition"
      :event-route="eventRoute"
      :event-markers="eventMarkers"
      :runner-data="runnerData"
    />
    <BottomSheet 
      :runner-data="runnerData"
      :event-route="eventRoute"
      :event-markers="eventMarkers"
      :loading="loading"
      :error="error"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import MapView from './MapView.vue'
import BottomSheet from './BottomSheet.vue'
import { runnerTrackingService } from '../application/RunnerTrackingService.js'
import { eventDataService } from '../application/EventDataService.js'
import { calculatePerformanceMetrics } from '../domain/services/PerformanceCalculator.js'
import { findLatestPositionByTime } from '../utils/gpsUtils.js'

export default {
  name: 'TrackingView',
  components: {
    MapView,
    BottomSheet
  },
  setup() {
    const route = useRoute()
    
    const eventName = computed(() => route.params.eventName)
    const runnerNumber = computed(() => route.params.runnerNumber)
    
    const runnerData = ref(null)
    const loading = ref(true)
    const error = ref(null)
    const eventRoute = ref([])
    const eventMarkers = ref([])
    
    // Real-time computed data that recalculates time-dependent values every second
    const currentTime = ref(Date.now()) // Reactive timestamp for triggering updates
    const realtimeRunnerData = computed(() => {
      if (!runnerData.value) return null
      
      const baseData = runnerData.value
      
      // If no startTime, return base data
      if (!baseData.startTime) return baseData
      
      // Reference currentTime to make this reactive to time updates
      // eslint-disable-next-line no-unused-vars
      const now = currentTime.value
      
      // Recalculate time-dependent performance metrics in real-time
      const updatedMetrics = calculatePerformanceMetrics(
        baseData.tracks || [],
        baseData.checkpoints || [],
        { startTime: baseData.startTime }
      )
      
      // Return merged data with updated real-time calculations
      return {
        ...baseData,
        ...updatedMetrics
      }
    })
    
    // Function to load event data (route and markers)
    const loadEventData = async () => {
      if (!eventName.value) return
      
      try {
        // Use new service layer
        const eventData = await eventDataService.getEventData(eventName.value)
        eventMarkers.value = eventData.markers
        eventRoute.value = eventData.route
        
      } catch (err) {
        console.warn('Error loading event data:', err)
        
        // For development, use mock data
        if (process.env.NODE_ENV === 'development') {
          console.warn('Using mock event data for development demonstration')
          const mockData = eventDataService.createMockEventData()
          eventRoute.value = mockData.route
          eventMarkers.value = mockData.markers
          
          console.log('Mock event data loaded:', { 
            routePoints: eventRoute.value.length,
            markers: eventMarkers.value.length 
          })
        } else {
          // Don't show error to user for event data - it's supplementary
          eventRoute.value = []
          eventMarkers.value = []
        }
      }
    }
    
    // Function to load runner data from API
    const loadRunnerData = async (showLoading = true) => {
      if (!eventName.value || !runnerNumber.value) {
        return
      }
      
      // Only show loading indicator on initial load or explicit request
      if (showLoading) {
        loading.value = true
      }
      error.value = null
      
      try {
        // Use new service layer with increased track limit for better elevation chart accuracy
        // For long routes (200km+), more track points are needed to properly represent the full distance
        const newRunnerData = await runnerTrackingService.getRunnerTrackingData(
          eventName.value, 
          runnerNumber.value,
          {
            includeTracks: 1,
            includeSplits: 1,
            limit: 100  // Increased from 100 to better support long routes and elevation charts
          }
        )
        
        // Update runner data while preserving any real-time calculated fields
        runnerData.value = newRunnerData
        
      } catch (err) {
        console.error('Error loading runner data:', err)
        
        // Fallback to mock data in case of API failure for development
        if (process.env.NODE_ENV === 'development') {
          console.warn('Using fallback mock data for development')
          runnerData.value = createMockData()
          error.value = null // Clear error when using fallback data
        } else {
          // Only show error on initial load, not on background refreshes
          if (showLoading) {
            error.value = err.message || 'Błąd podczas ładowania danych zawodnika'
          }
        }
      } finally {
        // Only clear loading indicator if it was shown
        if (showLoading) {
          loading.value = false
        }
      }
    }
    
    // Generate realistic GPS track data for a given distance
    const generateRealisticTrackData = (targetDistanceKm) => {
      const tracks = []
      const startLat = 50.7645 // Starting latitude
      const startLon = 16.2813 // Starting longitude
      const baseAltitude = 400 // Starting altitude
      
      let currentLat = startLat
      let currentLon = startLon
      let currentAltitude = baseAltitude
      let cumulativeDistance = 0
      let currentTime = new Date('2024-08-26T15:06:00.000Z')
      
      // Calculate how many points we need (aiming for points every ~200-300m for good resolution)
      const avgDistanceBetweenPoints = 0.25 // km between points
      const totalPoints = Math.ceil(targetDistanceKm / avgDistanceBetweenPoints)
      
      for (let i = 0; i < totalPoints && cumulativeDistance < targetDistanceKm; i++) {
        // Add some variation to make the route more realistic
        const bearing = 45 + Math.sin(i * 0.1) * 30 // Varying direction
        const distanceStep = avgDistanceBetweenPoints + (Math.random() - 0.5) * 0.1 // Small random variation
        
        // Calculate new position (simplified approximation)
        const deltaLat = (distanceStep * Math.cos(bearing * Math.PI / 180)) / 111.32 // ~111.32km per degree latitude
        const deltaLon = (distanceStep * Math.sin(bearing * Math.PI / 180)) / (111.32 * Math.cos(currentLat * Math.PI / 180))
        
        currentLat += deltaLat
        currentLon += deltaLon
        cumulativeDistance += distanceStep
        
        // Add realistic elevation changes - simulate hills and valleys
        const elevationChange = Math.sin(i * 0.05) * 100 + Math.cos(i * 0.03) * 50 + (Math.random() - 0.5) * 20
        currentAltitude = Math.max(300, baseAltitude + elevationChange) // Keep altitude reasonable
        
        // Generate realistic speed (2.5-5.5 km/h for hiking/trail running)
        const speed = 3.5 + Math.sin(i * 0.08) * 1.0 + (Math.random() - 0.5) * 0.5
        
        // Direction based on bearing
        const direction = bearing + (Math.random() - 0.5) * 10
        
        // Increment time (5-10 minutes between points depending on terrain)
        const timeIncrementMinutes = 5 + Math.random() * 5
        currentTime = new Date(currentTime.getTime() + timeIncrementMinutes * 60000)
        
        tracks.push({
          latitude: currentLat,
          longitude: currentLon, 
          altitude: Math.round(currentAltitude),
          speed: Math.round(speed * 10) / 10,
          time: currentTime.toISOString(),
          direction: Math.round(direction),
          distance: Math.round(cumulativeDistance * 10) / 10, // Distance in km with 1 decimal
          progress: Math.min((cumulativeDistance / targetDistanceKm) * 100, 100.0) // Progress as percentage (0.0 to 100.0)
        })
      }
      
      return tracks
    }

    // Create mock data for development fallback
    const createMockData = () => {
      const trackData = generateRealisticTrackData(18.5) // Generate 18.5km worth of track data (not finished yet)
      const latestPosition = findLatestPositionByTime(trackData)
      
      // Create mock splits data
      const mockSplits = [
        {
          name: 'START',
          checkpointId: 0,
          timeFromStartSeconds: 0,
          time: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
          distance: 0
        },
        {
          name: 'PZ Wijątru',
          checkpointId: 15,
          timeFromStartSeconds: 5 * 3600 + 4 * 60 + 33, // 5:04:33
          time: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
          distance: 8.5
        }
        // Runner hasn't reached Wielka Sowa yet - currently between checkpoints
      ]
      
      // Use PerformanceCalculator to compute metrics from track data
      const mockRunner = {
        startTime: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString() // 5 hours ago
      }
      const performanceMetrics = calculatePerformanceMetrics(trackData, mockSplits, mockRunner)
      
      return {
        firstName: 'Krzysztof',
        lastName: 'KOWALSKI',
        number: runnerNumber.value || '255',
        ...performanceMetrics, // Use calculated metrics instead of hardcoded values
        currentAltitude: '845 m',
        elevationGain: '+1247 m',
        gpsBattery: '78%',
        lastTrackTime: '2 min temu',
        eventInfo: `${eventName.value} - 21km ${new Date().toLocaleDateString('pl-PL')} 7:55:06`,
        checkpoints: [
          {
            id: 1,
            number: '0',
            name: 'START',
            distance: 'km 0.0',
            time: 'Pt. 17:06',
            splitTime: '0:00:00'
          },
          {
            id: 2,
            number: '15',
            name: 'PZ Wijątru',
            distance: 'km 8.5',
            time: 'Pt. 22:17',
            splitTime: '5:04:33'
          },
          {
            id: 3,
            number: '31',
            name: 'Wielka Sowa',
            distance: 'km 15.2',
            time: 'Sob. 01:05',
            splitTime: '7:55:06'
          }
        ],
        tracks: trackData,
        latestPosition: latestPosition
      }
    }
    
    // Load data on component mount
    onMounted(() => {
      loadRunnerData()
      loadEventData()
    })
    
    // Reload data when route params change
    watch([eventName, runnerNumber], () => {
      loadRunnerData()
    })
    
    // Reload event data when event name changes
    watch(eventName, () => {
      loadEventData()
    })
    
    // Auto-refresh data every 30 seconds for live updates
    let refreshInterval = null
    // Update time calculations every second for real-time display
    let timeUpdateInterval = null
    
    onMounted(() => {
      // Data refresh every 30 seconds
      refreshInterval = setInterval(() => {
        if (!loading.value) {
          loadRunnerData(false) // Don't show loading indicator during background refresh
        }
      }, 30000) // Refresh every 30 seconds
      
      // Time updates every second for real-time statistics
      timeUpdateInterval = setInterval(() => {
        currentTime.value = Date.now() // Update reactive timestamp to trigger computed recalculation
      }, 1000) // Update every second
    })
    
    // Cleanup intervals on unmount
    onUnmounted(() => {
      if (refreshInterval) {
        clearInterval(refreshInterval)
      }
      if (timeUpdateInterval) {
        clearInterval(timeUpdateInterval)
      }
    })
    
    return {
      eventName,
      runnerNumber,
      runnerData: realtimeRunnerData, // Use computed real-time data
      loading,
      error,
      eventRoute,
      eventMarkers
    }
  }
}
</script>

<style scoped>
.tracking-view {
  height: 100vh;
  width: 100vw;
  position: relative;
}
</style>